import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Button, Icon, Modal, message, Row, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../../../../../../services/apiInstance';
import { getRequiredVersionName } from '../../../../../../services/utils';
import useLibraryContents from '../../../hooks/contentManager/useLibraryContents';
import useContentActions from '../../../hooks/contentManager/useContentActions';
import useResolutionLogic from '../../../hooks/contentManager/useResolutionLogic';
import useModalState from '../../../hooks/contentManager/useModalState';
import useContentOfCompaign from '../../../hooks/contentManager/useContentOfCompaign';
import LibraryModals from '../../contents_library/LibraryModals';
import ContentsOfCampaignModals from '../../contents_of_campaign/ContentsOfCampaignModals';
import VersionTabsComponent from '../../step1_library/VersionTabsComponent';
import ResolutionsPanelComponent from '../../step1_library/ResolutionsPanelComponent';
import LibraryBrowserComponent from '../../step1_library/LibraryBrowserComponent';
import ModalContentComponent from '../../../../../shared/components/ModalContentComponent';
import OptionModal from '../../../../../shared/components/OptionModal';
import VersionAdsWellComponent, { WELL_DROPPABLE_ID } from '../../step1_library/VersionAdsWellComponent';

const NUMBERED_VERSION = /^V(\d+)$/i;

const nextVersionKey = (versions) => {
  const numbers = versions
    .map((v) => {
      const match = NUMBERED_VERSION.exec(v);
      return match ? parseInt(match[1], 10) : NaN;
    })
    .filter((n) => !isNaN(n));
  const maxNumbered = numbers.length ? Math.max(...numbers) : 0;
  let next = Math.max(versions.length + 1, maxNumbered + 1);
  while (versions.includes(`V${next}`)) next += 1;
  return `V${next}`;
};

const ScreenLibraryModalComponent = (props) => {
  const { t } = useTranslation();
  const {
    visible,
    onCancel,
    groupedDisplays,
    groupName,
    fromModal,
    applyRelationOnScreens,
    cart,
    listOfContentsInCampaign,
    setListOfContentsInCampaign,
    listResolutionWithContent,
    setListResolutionWithContent,
    customContents,
    updateCustomContents,
    updateListOfContentsInCampaign,
    toleranceResolution,
    toleranceResolutionInit,
    editCampaignActive,
    currentCampaign,
    creationType,
    isSuperUser,
    isPaymentStatusApproved,
    initialBackup,
    hasInitialBackup,
    setInitialBackup,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    remplaceContentForUniqueVersion,
    deleteContentFromCampaign,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    getPlaceholders,
    reportSingleData,
    libraryContents,
    loadingContentsbyCompany,
    uploadingAds,
    uploadAds,
  } = props;

  const [pendingRemoveVersion, setPendingRemoveVersion] = useState(null);
  const [previewingContent, setPreviewingContent] = useState(null);
  const [manualVersions, setManualVersions] = useState([]);

  const initialSnapshotRef = useRef({ listOfContentsInCampaign, customContents, listResolutionWithContent });

  const discardChangesAndClose = () => {
    const snapshot = initialSnapshotRef.current;
    setListOfContentsInCampaign(snapshot.listOfContentsInCampaign);
    updateCustomContents(snapshot.customContents);
    setListResolutionWithContent(snapshot.listResolutionWithContent);
    onCancel();
  };

  const libraryState = useLibraryContents({
    ...props,
    cardWidth: 220,
    cardGap: 16,
    reservedSlots: 0,
    initialVersion: getRequiredVersionName(listOfContentsInCampaign)
  });
  const contentActions = useContentActions(
    { ...props, groupedDisplays, contentSelected: null, exitModal: onCancel },
    libraryState
  );
  const modalState = useModalState({ deleteContentFromCampaign: contentActions.deleteContentScoped });

  const handleUploadFiles = (files, folderId) => {
    uploadAds(files, { folderId: folderId || (libraryState.selectTagContent && libraryState.selectTagContent.key) });
  };

  const [wellUploading, setWellUploading] = useState(false);

  const getFileDimensions = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const cleanup = (dimensions) => {
        URL.revokeObjectURL(url);
        resolve(dimensions);
      };
      if (file.type.startsWith('video')) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => cleanup({ width: video.videoWidth, height: video.videoHeight });
        video.onerror = () => cleanup(null);
        video.src = url;
      } else {
        const img = new Image();
        img.onload = () => cleanup({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => cleanup(null);
        img.src = url;
      }
    });

  const [pendingReplaceConfirm, setPendingReplaceConfirm] = useState(null);
  const confirmReplaceUpload = () => new Promise((resolve) => setPendingReplaceConfirm({ resolve }));

  const handleWellDropFiles = async (files) => {
    const folderId = libraryState.selectTagContent && libraryState.selectTagContent.key;
    setWellUploading(true);
    try {
      const filesToUpload = [];
      for (const file of files) {
        const dimensions = await getFileDimensions(file);
        const conflicts =
          dimensions && contentActions.wouldConflictInVersion(dimensions.width, dimensions.height, activeVersion);
        if (conflicts && !(await confirmReplaceUpload())) continue;
        filesToUpload.push(file);
      }
      if (!filesToUpload.length) return;

      const results = await Promise.all(
        filesToUpload.map((file) =>
          api.createContent({
            file,
            name: file.name.replace(/\.[^/.]+$/, ''),
            tags: folderId ? [folderId] : [],
          })
        )
      );
      const created = results.filter((resp) => resp.ok).map((resp) => resp.data);
      if (created.length < results.length) {
        message.error(t('An error occurred, please try again'));
      }
      created.forEach((content) => contentActions.addContents(content));
      if (created.length) libraryState.getLibraryContents(1, libraryState.selectTagContent);
    } finally {
      setWellUploading(false);
    }
  };

  const wasUploadingAds = useRef(false);
  useEffect(() => {
    if (wasUploadingAds.current && !uploadingAds) {
      libraryState.getLibraryContents(1, libraryState.selectTagContent);
    }
    wasUploadingAds.current = uploadingAds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingAds]);

  const resolution = useResolutionLogic({
    cart,
    listOfContentsInCampaign,
    customContents,
    toleranceResolution,
    listResolutionWithContent,
    editCampaignActive,
    currentCampaign,
    creationType,
    placeholders: [],
    isSuperUser,
    isPaymentStatusApproved,
    toleranceResolutionInit,
    initialBackup,
    hasInitialBackup,
    groupedDisplays,
    filterByContent: null,
    selectedContent: null,
    fromModal,
    groupName,
    setListResolutionWithContent,
    setListOfContentsInCampaign,
    updateCustomContents,
    remplaceContentForUniqueVersion,
    applyRelationOnScreens,
    setToleranceResolution: () => {},
    setInitialBackup,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    getPlaceholders,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    deletingContentFromLibrary: modalState.deletingContentFromLibrary,
    setDeletingContentFromLibrary: modalState.setDeletingContentFromLibrary,
    reportSingleData,
  });

  const contentOfCampaign = useContentOfCompaign({
    listOfContentsInCampaign,
    customContents,
    initialBackup,
    editCampaignActive,
    hasInitialBackup,
    groupedDisplays,
    cart,
    listResolutionWithContent,
    setListResolutionWithContent,
    updateListOfContentsInCampaign,
    updateCustomContents,
    applyRelationOnScreens,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    showModalCleanContent: modalState.showModalCleanContent,
    setResolutionFilterIndex: resolution.setResolutionFilterIndex,
    setVersionFilter: resolution.setVersionFilter,
    contentToCloneName: modalState.contentToCloneName,
    cancelModalCloneNameContent: modalState.cancelModalCloneNameContent,
  });

  const requiredVersionName = useMemo(
    () => getRequiredVersionName(listOfContentsInCampaign),
    [listOfContentsInCampaign]
  );

  const activeVersion = libraryState.selectVersion || requiredVersionName;

  useEffect(() => {
    resolution.setVersionFilter(libraryState.selectVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryState.selectVersion]);

  const handleSelectVersion = (version) => {
    libraryState.onChangeVersion(version);
  };

  const handleUnlinkContent = (content, relation) => {
    const displayIds = relation.displays.map((d) => d.id);
    setListOfContentsInCampaign(
      listOfContentsInCampaign.map((c) =>
        c.id === content.id && (c.content_version_name || 'V1') === (content.content_version_name || 'V1')
          ? { ...c, excludedDisplayIds: [...new Set([...(c.excludedDisplayIds || []), ...displayIds])] }
          : c
      )
    );
  };

  const handleRelinkContent = (content, relation) => {
    const displayIds = relation.displays.map((d) => d.id);
    setListOfContentsInCampaign(
      listOfContentsInCampaign.map((c) =>
        c.id === content.id && (c.content_version_name || 'V1') === (content.content_version_name || 'V1')
          ? { ...c, excludedDisplayIds: (c.excludedDisplayIds || []).filter((id) => !displayIds.includes(id)) }
          : c
      )
    );
  };

  const isLastActiveContentOfScreen = (relation, content) => {
    const displayIds = (relation && relation.displays ? relation.displays : groupedDisplays).map((d) => d.id);
    const contentVersion = content.content_version_name || 'V1';
    const affectedDisplayIds = displayIds.filter((displayId) => {
      const activeForDisplay = customContents.filter((cc) => cc.displayId === displayId && cc.active === 1);
      const hasThisContent = activeForDisplay.some((cc) => cc.content && cc.content.id === content.id);
      if (!hasThisContent) return false;

      const wasSavedBeforeThisSession = !!(
        initialBackup &&
        initialBackup.customContents &&
        initialBackup.customContents.some(
          (backupCc) =>
            backupCc.displayId === displayId &&
            backupCc.content &&
            backupCc.content.id === content.id &&
            (backupCc.content.content_version_name || 'V1') === contentVersion
        )
      );
      if (!wasSavedBeforeThisSession) return false;

      if (activeForDisplay.length === 1) return true;

      if (contentVersion === requiredVersionName) {
        const activeRequiredForDisplay = activeForDisplay.filter(
          (cc) => (cc.content.content_version_name || 'V1') === requiredVersionName
        );
        return activeRequiredForDisplay.length === 1;
      }

      return false;
    });
    return affectedDisplayIds
      .map((displayId) => {
        const display = groupedDisplays.find((d) => d.id === displayId);
        return display ? display.name : null;
      })
      .filter(Boolean);
  };

  const scopedContentIds = useMemo(() => {
    const ids = new Set();
    resolution.listResolution.forEach((relation) => {
      (relation.contents || []).forEach((c) => ids.add(c.id));
    });
    return ids;
  }, [resolution.listResolution]);

  const initialContentIds = useMemo(
    () => new Set(initialSnapshotRef.current.listOfContentsInCampaign.map((c) => c.id)),
    []
  );

  const scopedContents = useMemo(
    () =>
      listOfContentsInCampaign.filter(
        (c) =>
          !c.isPlaceholder &&
          (scopedContentIds.has(c.id) ||
            (!initialContentIds.has(c.id) && (c.content_version_name || 'V1') === activeVersion))
      ),
    [listOfContentsInCampaign, scopedContentIds, activeVersion, initialContentIds]
  );

  const versions = useMemo(() => {
    const fromContent = scopedContents
      .map((c) => c.content_version_name || 'V1')
      .filter((v, i, arr) => arr.indexOf(v) === i);
    return [requiredVersionName, ...fromContent, ...manualVersions, activeVersion].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );
  }, [scopedContents, requiredVersionName, manualVersions, activeVersion]);

  const handleAddVersion = () => {
    const next = nextVersionKey(versions);
    setManualVersions((prev) => [...prev, next]);
    handleSelectVersion(next);
  };

  const versionCounts = useMemo(() => {
    const counts = {};
    scopedContents.forEach((c) => {
      const v = c.content_version_name || 'V1';
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  }, [scopedContents]);

  const scopedWellContents = useMemo(
    () => scopedContents.filter((c) => (c.content_version_name || 'V1') === activeVersion),
    [scopedContents, activeVersion]
  );

  const orphanContentIds = useMemo(
    () => new Set(scopedContents.filter((c) => !scopedContentIds.has(c.id)).map((c) => c.id)),
    [scopedContents, scopedContentIds]
  );

  const unmatchedVersions = useMemo(() => {
    const set = new Set();
    scopedContents.forEach((c) => {
      if (orphanContentIds.has(c.id)) set.add(c.content_version_name || 'V1');
    });
    return set;
  }, [scopedContents, orphanContentIds]);

  const inactiveVersions = useMemo(() => {
    const groupedDisplayIds = (groupedDisplays || []).map((d) => d.id);
    const set = new Set();
    versions.forEach((v) => {
      if (v === requiredVersionName) return;
      const relationsInVersion = customContents.filter(
        (cc) =>
          cc.content &&
          (cc.content.content_version_name || 'V1') === v &&
          groupedDisplayIds.includes(cc.displayId)
      );
      if (relationsInVersion.length > 0 && !relationsInVersion.some((cc) => cc.active === 1)) {
        set.add(v);
      }
    });
    return set;
  }, [versions, customContents, requiredVersionName, groupedDisplays]);

  const handleRemoveVersion = (version) => {
    if (version === requiredVersionName) return;
    const willHardDelete = contentActions.willHardDeleteVersion(version);
    setPendingRemoveVersion({ version, willHardDelete });
  };

  const handlePreviewContent = (content, relation) => {
    setPreviewingContent({ content, relation });
  };

  const confirmRemoveVersion = () => {
    const { version } = pendingRemoveVersion;
    contentActions.deleteVersion(version);
    if (activeVersion === version) handleSelectVersion(requiredVersionName);
    setPendingRemoveVersion(null);
  };

  const rawContents = (libraryContents && libraryContents.data) || [];

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const handleDragEnd = ({ active, over }) => {
    if (!over || over.id !== WELL_DROPPABLE_ID) return;
    if (inactiveVersions.has(activeVersion)) return;
    const rawContent = active.data.current && active.data.current.rawContent;
    if (rawContent) contentActions.addContents(rawContent);
  };

  const panelStyle = {
    background: '#FFFFFF',
    borderRadius: 4,
    marginBottom: 16,
    border: '1px solid #f0f0f0',
    padding: 24,
  };

  return (
    <Modal
      title={
        <Row type="flex" style={{ gap: 4 }}>
          <span>{t('Manage ads and screens')}</span>
          <strong className="first-capitalize">{groupName}</strong>
        </Row>
      }
      visible={visible}
      onCancel={discardChangesAndClose}
      footer={[
        <Button key="cancel" onClick={discardChangesAndClose}>
          {t('Cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={onCancel}>
          {t('Save')}
        </Button>,
      ]}
      width={1280}
      destroyOnClose
    >
      <DndContext sensors={dndSensors} onDragEnd={handleDragEnd}>
        <div className="library-step">
          <div style={panelStyle}>
            <LibraryBrowserComponent
              containerRef={libraryState.containerRef}
              rawContents={rawContents}
              loading={loadingContentsbyCompany}
              folders={libraryState.tagsContents}
              selectedFolder={libraryState.selectTagContent}
              onSelectFolder={(folderId) => {
                const folder = folderId != null ? libraryState.tagsContents.find((f) => f.id === folderId) : null;
                libraryState.onChangeFolder(folder ? { key: folder.id, label: folder.name } : { key: null });
              }}
              onSearch={(value) => libraryState.onSearch({ target: { value } })}
              onAddContent={contentActions.addContents}
              uploadProps={libraryState.uploadProps}
              onAddAllFromFolder={contentActions.triggerAddAllFromFolder}
              loadingAddAll={contentActions.loadingAddAll}
              isFolderUsed={contentActions.isFolderUsed}
              totalContent={libraryState.totalContent}
              pageContents={libraryState.pageContents}
              lengthPage={libraryState.lengthPage}
              onPageChange={libraryState.changePageContents}
              onPreviewAd={contentActions.showModalContent}
              typeFilter={libraryState.typeFilter}
              onTypeFilterChange={libraryState.onTypeFilterChange}
              sort={libraryState.sort}
              onSortChange={libraryState.onSortChange}
              onUploadFiles={handleUploadFiles}
              uploading={uploadingAds}
              checkContentsAdded={contentActions.checkContentsAdded}
              canUploadToFolder={libraryState.isOwnerDefaultContent(libraryState.selectTagContent)}
              onCreateFolder={libraryState.createFolder}
              onRenameFolder={libraryState.renameFolder}
              onDeleteFolders={libraryState.deleteFolders}
              folderSubmitting={libraryState.folderSubmitting}
              rowCount={libraryState.rowCount}
              rowOptions={libraryState.rowOptions}
              onRowCountChange={libraryState.handleRowCountChange}
              isMobile={libraryState.isMobile()}
              activeVersion={activeVersion}
              onSelectVersion={handleSelectVersion}
              showVersionSelect
            />
          </div>

          <div style={panelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <h2 style={{ color: '#1890ff', margin: 0 }}>{t('Ads for the campaign')}</h2>
              <Tooltip title={t('Tolerance set for this campaign; adjust it from the Ad library step')}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: '4px 16px',
                    background: '#fff',
                    color: 'rgba(0,0,0,0.65)',
                  }}
                >
                  <Icon type="info-circle" />
                  <span>{t('Tolerance')}</span>
                  <span style={{ fontWeight: 600 }}>{toleranceResolution}%</span>
                </div>
              </Tooltip>
            </div>

            <VersionTabsComponent
              versions={versions}
              activeVersion={activeVersion}
              counts={versionCounts}
              inactiveVersions={inactiveVersions}
              unmatchedVersions={unmatchedVersions}
              requiredVersionName={requiredVersionName}
              onSelectVersion={handleSelectVersion}
              onAddVersion={handleAddVersion}
              onRemoveVersion={handleRemoveVersion}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
              <div>
                <VersionAdsWellComponent
                  heading={t('Ads in {{version}}', { version: activeVersion })}
                  contents={scopedWellContents}
                  onRename={contentOfCampaign.handleContent}
                  onCloneName={modalState.showModalCloneNameContent}
                  onPreview={handlePreviewContent}
                  onDeleteRequest={modalState.showModalDeleteContentRelation}
                  listResolution={resolution.listResolution}
                  isTheLastOfTheRelationship={isLastActiveContentOfScreen}
                  isPaymentStatusApproved={isPaymentStatusApproved}
                  customContents={customContents}
                  readOnly={inactiveVersions.has(activeVersion)}
                  onUploadFiles={handleWellDropFiles}
                  uploading={wellUploading}
                  orphanContentIds={orphanContentIds}
                />
              </div>

              <div>
                {resolution.hasDifferentResolution && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, color: '#888', fontStyle: 'italic' }}>
                    <Icon type="warning" style={{ color: '#faad14', marginTop: 2 }} />
                    <span>{t('There are ads that do not adapt correctly to the resolutions. You can continue or select ads from closer resolutions.')}</span>
                  </div>
                )}
                <ResolutionsPanelComponent
                  listResolution={resolution.listResolution}
                  activeVersion={activeVersion}
                  versions={versions}
                  isTheLastOfTheRelationship={isLastActiveContentOfScreen}
                  onSelectVersion={handleSelectVersion}
                  onUnlinkContent={handleUnlinkContent}
                  onRelinkContent={handleRelinkContent}
                  onPreviewContent={handlePreviewContent}
                  versionContents={listOfContentsInCampaign.filter(
                    (c) => (c.content_version_name || 'V1') === activeVersion
                  )}
                  isPaymentStatusApproved={isPaymentStatusApproved}
                />
              </div>
            </div>
          </div>

          <OptionModal
            title={t('Atention!')}
            body={t('This ad will be unlinked from the group of screens for this resolution.')}
            visible={!!pendingRemoveVersion}
            okText={t('Continue')}
            cancelText={t('Cancel')}
            onOk={confirmRemoveVersion}
            onCancel={() => setPendingRemoveVersion(null)}
          />

          <OptionModal
            title={t('Remplace version')}
            body={t('A version with that resolution already exists, you want to replace the content')}
            visible={!!pendingReplaceConfirm}
            okText={t('Remplace')}
            cancelText={t('Cancel')}
            onOk={() => {
              pendingReplaceConfirm.resolve(true);
              setPendingReplaceConfirm(null);
            }}
            onCancel={() => {
              pendingReplaceConfirm.resolve(false);
              setPendingReplaceConfirm(null);
            }}
          />

          <ModalContentComponent
            visible={!!previewingContent}
            content={previewingContent && previewingContent.content}
            relation={previewingContent && previewingContent.relation}
            onCancel={() => setPreviewingContent(null)}
          />

          <LibraryModals
            visibleModalContent={contentActions.visibleModalContent}
            visibleModalDeleteContent={contentActions.visibleModalDeleteContent}
            isModalOpen={contentActions.isModalOpen}
            isModalCreateVersionOpen={contentActions.isModalCreateVersionOpen}
            visibleCanvaModal={contentActions.visibleCanvaModal}
            alertModalVisible={contentActions.alertModalVisible}
            conflictModalOpen={contentActions.conflictModalOpen}
            contentModal={contentActions.contentModal}
            selectTagContent={libraryState.selectTagContent}
            getChangeAdDescription={contentActions.getChangeAdDescription}
            conflictGroups={contentActions.conflictGroups}
            existingByResolution={contentActions.existingByResolution}
            paymentStatus={contentActions.paymentStatus}
            cancelModalContent={contentActions.cancelModalContent}
            updateContent={contentActions.updateContent}
            showModalDeleteContent={contentActions.showModalDeleteContent}
            replaceVersion={contentActions.replaceVersion}
            handleCancel={contentActions.handleCancel}
            createVersion={contentActions.createVersion}
            handleModalCreateVersionCancel={contentActions.handleModalCreateVersionCancel}
            handleChangeAd={contentActions.handleChangeAd}
            setAlertModalVisible={contentActions.setAlertModalVisible}
            setVisibleCanvaModal={contentActions.setVisibleCanvaModal}
            confirmAddAll={contentActions.confirmAddAll}
            cancelAddAll={contentActions.cancelAddAll}
          />

          <ContentsOfCampaignModals
            visibleModalDeleteContentRelation={modalState.visibleModalDeleteContentRelation}
            showModalDeleteContentRelation={modalState.showModalDeleteContentRelation}
            deleteContentOfListRelation={modalState.deleteContentOfListRelation}
            visibleModalCleanContent={modalState.visibleModalCleanContent}
            showModalCleanContent={modalState.showModalCleanContent}
            cleanProgress={contentOfCampaign.cleanProgress}
            visibleModalCloneNameContent={modalState.visibleModalCloneNameContent}
            cancelModalCloneNameContent={modalState.cancelModalCloneNameContent}
            cloneNameToAllVersions={contentOfCampaign.cloneNameToAllVersions}
            visibleModalContent={modalState.visibleModalContent}
            cancelModalContent={modalState.cancelModalContent}
            contentModal={modalState.contentModal}
          />
        </div>
      </DndContext>
    </Modal>
  );
};

export default ScreenLibraryModalComponent;
